import React, { useState, useEffect } from 'react'
import messages from './messages'
import { FormattedMessage, useIntl } from 'react-intl'
import { Full as Loading } from 'components/Loading'
import Icon from 'components/Icon'

const PublicDataDeletionStatusPage = ({ location }) => {
  const intl = useIntl()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [data, setData] = useState(null)

  const searchParams = new URLSearchParams(location.search)
  const code = searchParams.get('code')

  useEffect(() => {
    if (!code) {
      setError('No confirmation code provided')
      setLoading(false)
      return
    }

    fetch(`/rest/v2/whatsappdatadeletionstatus?code=${encodeURIComponent(code)}`)
      .then((response) => response.json())
      .then(({ data, success, errors }) => {
        if (!success) {
          setError(errors?.join(', ') || 'Failed to verify deletion status')
          setLoading(false)
          return
        }

        setData(data)
        setLoading(false)
      })
      .catch((error) => {
        setError(error.message || 'Failed to verify deletion status')
        setLoading(false)
      })
  }, [code])

  const formatDate = (timestamp) => {
    if (!timestamp) return ''
    const date = new Date(timestamp * 1000)
    return intl.formatDate(date, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
    })
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-500 to-purple-800">
        <Loading />
      </div>
    )
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-500 to-purple-800 p-5">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full p-10 text-center">
        {data?.conversationFound ? (
          <>
            <div className="w-20 h-20 mx-auto mb-6 bg-green-500 rounded-full flex items-center justify-center text-white">
              <Icon icon={['fas', 'check']} className="text-5xl" />
            </div>
            <h1 className="text-3xl font-semibold mb-4 text-gray-900">
              <FormattedMessage {...messages.confirmed} />
            </h1>
            <p className="text-base leading-relaxed text-gray-600 mb-3">
              <FormattedMessage {...messages.confirmedDescription} />
            </p>
            {data.deletedAt && (
              <p className="text-gray-700 mb-6">
                <FormattedMessage {...messages.deletionCompletedOn} />{' '}
                <strong>{formatDate(data.deletedAt)}</strong>
              </p>
            )}

            <div className="bg-gray-100 rounded-lg p-4 mb-6">
              <strong className="block mb-2 text-gray-900">
                <FormattedMessage {...messages.confirmationCode} />
              </strong>
              <code className="text-sm font-mono text-gray-700 break-all">
                {code}
              </code>
            </div>

            <p className="text-sm text-gray-500">
              <FormattedMessage {...messages.confirmationCodeDescription} />
            </p>
          </>
        ) : (
          <>
            <div className="w-20 h-20 mx-auto mb-6 bg-red-500 rounded-full flex items-center justify-center text-white">
              <Icon icon={['fas', 'question']} className="text-5xl" />
            </div>
            <h1 className="text-3xl font-semibold mb-4 text-gray-900">
              <FormattedMessage {...messages.notFound} />
            </h1>
            <p className="text-base leading-relaxed text-gray-600 mb-6">
              <FormattedMessage {...messages.notFoundDescription} />
            </p>
            <div className="bg-gray-100 rounded-lg p-4 mb-6">
              <strong className="block mb-2 text-gray-900">
                <FormattedMessage {...messages.providedCode} />
              </strong>
              <code className="text-sm font-mono text-gray-700 break-all">
                {code}
              </code>
            </div>
            <p className="text-sm text-gray-500">
              <FormattedMessage {...messages.notFoundDetails} />
            </p>
          </>
        )}
        <div className="mt-8 pt-6 border-t border-gray-200">
          <p className="text-sm text-gray-400">
            <a
              href="https://www.voicearchive.com"
              target="_blank"
              rel="noopener noreferrer"
              className="text-purple-600 hover:underline"
            >
              <FormattedMessage {...messages.voiceArchive} />
            </a>
            {' | '}
            <a
              href="https://www.voicearchive.com/privacy-policy"
              target="_blank"
              rel="noopener noreferrer"
              className="text-purple-600 hover:underline"
            >
              <FormattedMessage {...messages.privacyPolicy} />
            </a>
          </p>
        </div>
      </div>
    </div>
  )
}

export default PublicDataDeletionStatusPage
